import { FC } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";

import { t } from "i18n/i18n";
import { FormField } from "mds/components/FormField";
import { TextArea } from "mds/components/TextArea";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { storeApi, useAppSelector } from "store/index";
import { selectAssessmentAIFeedbackStyle, selectCurrentUserId } from "store/selectors";
import { toastSuccessMessage } from "utils/alerts";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "utils/amplitude";
import { rollbarAndLogError } from "utils/logger";
import { isAssessmentAIFeedbackStyleEnabled } from "utils/waffle-flags";

const MAX_ASSESSMENT_PROMPT_LEN = 50_000;

type FormValues = {
  assessment_ai_feedback_style: string;
};

/** Account Settings AI tab — edits optional `assessment_ai_feedback_style` (waffle-gated). */
export const AISettingsTab: FC<{ onClose: () => void; readOnly?: boolean }> = ({
  onClose,
  readOnly = false,
}) => {
  const userId = useAppSelector(selectCurrentUserId);
  const assessmentAIFeedbackStyle = useAppSelector(selectAssessmentAIFeedbackStyle);
  const form = useForm<FormValues>({
    defaultValues: {
      assessment_ai_feedback_style: assessmentAIFeedbackStyle ?? "",
    },
  });

  const onSubmit: SubmitHandler<FormValues> = async ({ assessment_ai_feedback_style }) => {
    if (readOnly || !userId) {
      onClose();
      return;
    }
    const next = assessment_ai_feedback_style.trim() || null;
    const currentStored = assessmentAIFeedbackStyle ? assessmentAIFeedbackStyle.trim() : null;
    if (next === currentStored) {
      onClose();
      return;
    }
    trackEvent("Account Settings - Save AI changes", BUTTON_EVENT_CATEGORY);
    try {
      await storeApi.users.partial_update({
        id: userId,
        assessment_ai_feedback_style: next,
      });
      toastSuccessMessage(t("success.toasts.updated_account_settings"));
      onClose();
    } catch (error: unknown) {
      rollbarAndLogError("assessment_ai_feedback_style partial_update", error);
    }
  };

  if (!isAssessmentAIFeedbackStyleEnabled()) {
    return null;
  }

  return (
    <FormProvider {...form}>
      <form noValidate onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          disabled={readOnly}
          helper={t("home.settings.assessment_ai_feedback_style.description")}
          label={t("home.settings.assessment_ai_feedback_style.title")}
          name="assessment_ai_feedback_style"
        >
          {(controlProps) => (
            <TextArea
              {...form.register("assessment_ai_feedback_style")}
              {...controlProps}
              maxLength={MAX_ASSESSMENT_PROMPT_LEN}
            />
          )}
        </FormField>

        <div className="flex justify-end gap-2 pt-8">
          <SquareButton kind="tertiary" size="m" title={t("common.cancel")} onClick={onClose}>
            {t("common.cancel")}
          </SquareButton>

          <SquareButton kind="accent" size="m" title={t("common.save_changes")} type="submit">
            {t("common.save_changes")}
          </SquareButton>
        </div>
      </form>
    </FormProvider>
  );
};
